import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { useJsApiLoader } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton } from '../common/Buttons';
import Recaptcha from '../common/Recaptcha';
import StyledInput from '../common/StyledInput';
import './style.css';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const PLANS = ['Starter', 'Pro', 'Commerce', 'Custom'];

export default function Net30ApplicationSection() {
  const navigate = useNavigate();
  const autocompleteRef = useRef(null);
  const addressRef = useRef(null);
  const recaptchaRef = useRef();

  const [form, setForm] = useState({
    legalName: '',
    tradeName: '',
    ein: '',
    formationDate: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    contactName: '',
    position: '',
    email: '',
    phone: '',
    revenue: '',
    plan: '',
    comments: '',
    neverMissInvoice: false,
    agreeAutoPayment: false,
    agreeTerms: false
  });

  const [recaptchaVerified, setRecaptchaVerified] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckbox = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace && autocompleteRef.current.getPlace();
    if (!place) return;

    const components = place.address_components || [];
    const get = (types) =>
      components.find((c) => types.every((t) => c.types.includes(t)))?.long_name || '';

    const streetNumber = get(['street_number']);
    const route = get(['route']);
    const city = get(['locality']) || get(['sublocality', 'political']);
    const state = get(['administrative_area_level_1']);
    const zip = get(['postal_code']);

    const address = [streetNumber, route].filter(Boolean).join(' ').trim();

    setForm((prev) => ({
      ...prev,
      address,
      city,
      state,
      zip
    }));
  }, []);

  useEffect(() => {
    if (!isLoaded || !addressRef.current) return;

    if (autocompleteRef.current && autocompleteRef.current.removeListener) {
      autocompleteRef.current.removeListener(
        'gmp-placeautocomplete-placechanged',
        handlePlaceChanged
      );
    }

    if (window.google && window.google.maps && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressRef.current, {
        fields: ['address_components', 'geometry'],
        types: ['address'],
      });
      autocompleteRef.current = autocomplete;
      autocomplete.addListener('place_changed', handlePlaceChanged);

      return () => {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      };
    }
  }, [isLoaded, handlePlaceChanged]);

  // Verifica que los campos requeridos estén llenos y válidos (no vacíos)
  const allRequiredFieldsFilled = [
    form.legalName,
    form.ein,
    form.formationDate,
    form.address,
    form.city,
    form.state,
    form.zip,
    form.contactName,
    form.position,
    form.email,
    form.phone
  ].every(val => val && String(val).trim().length > 0);

  // Condición de habilitado
  const canSubmit = allRequiredFieldsFilled && form.agreeAutoPayment && form.agreeTerms && recaptchaVerified;

  const handleRecaptcha = (token) => setRecaptchaVerified(!!token);
  const handleRecaptchaExpired = () => setRecaptchaVerified(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("SUBMIT", { canSubmit });

    if (!canSubmit) {
      alert('Please complete all required fields and consents.');
      return;
    }

    const recaptchaToken = recaptchaRef.current?.getToken();
    const payload = { ...form, recaptchaToken };
    const appsScriptApiKey = process.env.REACT_APP_APPS_SCRIPT_ENDPOINT;

    try {
      const response = await fetch(appsScriptApiKey, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status === 'success') {
        navigate('/checkout/success');
      } else {
        alert('Failed to send application.');
      }
    } catch (error) {
      console.error(error);
      alert('Error sending application.');
    }
  };

  return (
    <section className="net30app-section" id="net30-form">
      <Container>
        <div className="text-center mb-5">
          <h2 className="net30app-title">
            NET 30 Application
          </h2>
          <div className="net30app-lead">
            Launch now, pay later. Complete the form to qualify for our NET 30 line.
          </div>
        </div>
        <form noValidate onSubmit={handleSubmit} autoComplete="off">
          {/* Company Information */}
          <h5 className="net30app-subtitle">Company Information</h5>
          <Row>
            <Col md="6">
              <StyledInput
                label="Legal Business Name*"
                id="legalName"
                required
                value={form.legalName}
                onChange={handleChange('legalName')}
                name="legalName"
                type="text"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Trade Name / DBA"
                id="tradeName"
                value={form.tradeName}
                onChange={handleChange('tradeName')}
                name="tradeName"
                type="text"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Tax ID / EIN*"
                id="ein"
                required
                value={form.ein}
                onChange={handleChange('ein')}
                name="ein"
                type="text"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Date of Formation*"
                id="formationDate"
                required
                value={form.formationDate}
                onChange={handleChange('formationDate')}
                name="formationDate"
                type="date"
              />
            </Col>
          </Row>

          {/* Business Address */}
          <h5 className="net30app-subtitle">Business Address</h5>
          <Row>
            <Col md="12">
              <StyledInput
                label="Street Address*"
                id="address"
                required
                value={form.address}
                onChange={handleChange('address')}
                name="address"
                type="text"
                inputRef={addressRef}
              />
            </Col>
            <Col md="12">
              <StyledInput
                label="Address Line 2 (optional)"
                id="addressLine2"
                value={form.addressLine2}
                onChange={handleChange('addressLine2')}
                name="addressLine2"
                type="text"
              />
            </Col>
            <Col md="4">
              <StyledInput
                label="City*"
                id="city"
                required
                value={form.city}
                onChange={handleChange('city')}
                name="city"
                type="text"
              />
            </Col>
            <Col md="4">
              <div className="mui-form-group">
                <select
                  id="state"
                  className="mui-input"
                  required
                  value={form.state}
                  onChange={handleChange('state')}
                  name="state"
                >
                  <option value="" disabled>Select State</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <label htmlFor="state" className="mui-label">State*</label>
              </div>
            </Col>
            <Col md="4">
              <StyledInput
                label="ZIP Code*"
                id="zip"
                required
                value={form.zip}
                onChange={handleChange('zip')}
                name="zip"
                type="text"
              />
            </Col>
          </Row>

          {/* Authorized Contact */}
          <h5 className="net30app-subtitle">Authorized Contact</h5>
          <Row>
            <Col md="6">
              <StyledInput
                label="Full Name*"
                id="contactName"
                required
                value={form.contactName}
                onChange={handleChange('contactName')}
                name="contactName"
                type="text"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Position*"
                id="position"
                required
                value={form.position}
                onChange={handleChange('position')}
                name="position"
                type="text"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Email*"
                id="email"
                required
                value={form.email}
                onChange={handleChange('email')}
                name="email"
                type="email"
              />
            </Col>
            <Col md="6">
              <StyledInput
                label="Phone Number*"
                id="phone"
                required
                value={form.phone}
                onChange={handleChange('phone')}
                name="phone"
                type="text"
              />
            </Col>
          </Row>

          {/* Financial Info */}
          <h5 className="net30app-subtitle">Financial Information</h5>
          <Row>
            <Col md="6">
              <StyledInput
                label="Estimated Annual Revenue (USD)"
                id="revenue"
                value={form.revenue}
                onChange={handleChange('revenue')}
                name="revenue"
                type="text"
              />
            </Col>
            <Col md="6">
              <div className="mui-form-group">
                <select
                  id="plan"
                  className="mui-input"
                  value={form.plan}
                  onChange={handleChange('plan')}
                  name="plan"
                >
                  <option value="">Select Plan</option>
                  {PLANS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <label htmlFor="plan" className="mui-label">Desired Hosting Plan</label>
              </div>
            </Col>
            <Col md="12">
              <div className="mui-form-group">
                <textarea
                  id="comments"
                  className="mui-input"
                  value={form.comments}
                  onChange={handleChange('comments')}
                  rows={4}
                  name="comments"
                  style={{ minHeight: 80, resize: 'vertical' }}
                />
                <label htmlFor="comments" className="mui-label">Additional Comments</label>
              </div>
            </Col>
          </Row>

          {/* Consents */}
          <div className="net30app-checkboxes mb-4">
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="neverMissInvoice"
                checked={form.neverMissInvoice}
                onChange={handleCheckbox('neverMissInvoice')}
              />
              <label className="form-check-label" htmlFor="neverMissInvoice">
                <strong>Never Miss An Invoice</strong><br />
                I agree to receive text message reminders about SJ Social Media invoices.
                Message and data rates may apply. Opt-out anytime.
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="agreeAutoPayment"
                checked={form.agreeAutoPayment}
                onChange={handleCheckbox('agreeAutoPayment')}
                required
              />
              <label className="form-check-label" htmlFor="agreeAutoPayment">
                <strong>Auto-payment option</strong><br />
                I understand and agree that each invoice will be automatically charged to the registered card
                via Stripe at the start of each billing cycle.
                Card details are securely stored by Stripe for this purpose. Invoices and payment reminders
                will be sent electronically to the email address provided in the application.
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="agreeTerms"
                checked={form.agreeTerms}
                onChange={handleCheckbox('agreeTerms')}
                required
              />
              <label className="form-check-label" htmlFor="agreeTerms">
                <strong>Terms of Service and Privacy Policy</strong><br />
                I have read and agreed to the Terms of Service and Privacy Policy.
              </label>
            </div>
          </div>

          {/* Recaptcha */}
          <div className="text-center mb-4">
            <Recaptcha
              ref={recaptchaRef}
              onVerify={handleRecaptcha}
              onExpire={handleRecaptchaExpired}
            />
          </div>

          <div className="text-center mb-4">
            <PrimaryButton
              type="submit"
              disabled={!canSubmit}
            >
              📩 Submit Application
            </PrimaryButton>
          </div>
        </form>
      </Container>
    </section>
  );
}
